<template>
  <div class="tl-page">
    <!-- ── Filter/Header Bar ── -->
    <div class="tl-filters">
      <div class="tl-filters__selects">
        <h5 class="tl-page-title">
          <svg class="tl-title-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          Cities
        </h5>
        <span class="tl-count">{{ filteredCities.length }} Total</span>
      </div>

      <div class="tl-filters__actions">
        <div class="tl-search">
          <svg class="tl-search__icon" viewBox="0 0 20 20" fill="none">
            <circle cx="8.5" cy="8.5" r="5.5" stroke="#9ca3af" stroke-width="1.6" />
            <path d="M13 13l3 3" stroke="#9ca3af" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <input type="text" v-model="searchTerm" class="tl-search__input" placeholder="Search cities..." />
        </div>
        <button class="tl-btn" :class="showForm ? 'tl-btn--danger' : 'tl-btn--primary'" @click="toggleForm">
          {{ showForm ? '✕ Cancel' : '+ Add City' }}
        </button>
      </div>
    </div>

    <!-- ── Add City Form ── -->
    <div v-if="showForm" class="tl-form-panel">
      <div class="tl-card">
        <div class="tl-card__header">Add New City</div>
        <div class="tl-card__body">
          <form @submit.prevent="handleSubmit" class="tl-form-row">
            <div class="tl-form-group">
              <label class="tl-label">City Name</label>
              <input type="text" v-model="form.city" class="tl-input" placeholder="e.g. Vienna" required autofocus />
            </div>
            <div class="tl-form-actions">
              <button type="submit" class="tl-btn tl-btn--primary">Save City</button>
              <button type="button" class="tl-btn tl-btn--ghost" @click="resetForm">Reset</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- ── Cities Table ── -->
    <div class="tl-table-card">
      <div class="tl-table-scroll">
        <table class="tl-table">
          <thead>
            <tr>
              <th class="tl-th tl-th--id">#</th>
              <th class="tl-th text-left">City Name</th>
              <th class="tl-th">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="city in filteredCities" :key="city.id" class="tl-row">
              <td class="tl-td tl-td--id">{{ city.id }}</td>
              <td class="tl-td tl-td--name">
                <div class="tl-city-cell">
                  {{ city.attributes.city }}
                </div>
              </td>
              <td class="tl-td">
                <span class="tl-pill tl-pill--green">Active</span>
              </td>
            </tr>
            <tr v-if="filteredCities.length === 0">
              <td colspan="3" class="tl-empty">
                {{ searchTerm ? 'No cities match your search.' : 'No cities added yet.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cities: [],
      showForm: false,
      form: { city: '' },
      searchTerm: '',
    };
  },
  created() {
    this.fetchCities();
  },
  computed: {
    filteredCities() {
      return this.cities.filter(city =>
        city.attributes.city.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }
  },
  methods: {
    async fetchCities() {
      try {
        const { data } = await this.$xhr.get("/api/cities?populate=*&pagination[pageSize]=1000&sort[0]=city:asc");
        this.cities = data.data;
      } catch (err) {
        console.error('Failed to fetch cities:', err);
      }
    },
    async handleSubmit() {
      const cityExists = this.cities.some(
        city => city.attributes.city.toLowerCase() === this.form.city.toLowerCase()
      );

      if (cityExists) {
        this.$bvToast.toast('City already exists.', {
          title: 'Duplicate',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
      } else {
        try {
          const formData = { city: this.form.city };
          await this.$xhr.post("/api/cities", { data: formData });
          await this.fetchCities();
          this.resetForm();
          this.$bvToast.toast('City added successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } catch (err) {
          console.error('Failed to add city:', err);
        }
      }
    },
    toggleForm() {
      this.showForm = !this.showForm;
      if (!this.showForm) this.resetForm();
    },
    resetForm() {
      this.form.city = '';
    },
  },
};
</script>

<style scoped>
/* ────────────── Page ────────────── */
.tl-page {
  padding: 16px 18px;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}

/* ────────────── Header/Filter Bar ────────────── */
.tl-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
}

.tl-page-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.tl-title-icon {
  width: 18px;
  color: #E2923D;
}

.tl-count {
  background: #fff3e0;
  color: #b45309;
  font-size: .75rem;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 20px;
  border: 1px solid #fde68a;
}

.tl-filters__selects {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tl-filters__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Search */
.tl-search {
  position: relative;
}

.tl-search__icon {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  width: 15px;
}

.tl-search__input {
  height: 32px;
  padding: 0 10px 0 30px;
  min-width: 220px;
  font-size: .82rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #f9fafb;
  outline: none;
  transition: border-color .15s;
}

.tl-search__input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
  background: #fff;
}

/* Buttons */
.tl-btn {
  height: 32px;
  padding: 0 14px;
  font-size: .8rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all .15s;
  white-space: nowrap;
}

.tl-btn--primary {
  background: #E2923D;
  color: #fff;
}

.tl-btn--danger {
  background: #fee2e2;
  color: #ef4444;
  border: 1px solid #fecaca;
}

.tl-btn--ghost {
  background: #f3f4f6;
  color: #4b5563;
}

.tl-btn:hover:not(.tl-btn--danger) {
  opacity: .9;
}

.tl-btn--danger:hover {
  background: #fecaca;
}

/* ────────────── Form Panel ────────────── */
.tl-form-panel {
  margin-bottom: 16px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.tl-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  max-width: 600px;
}

.tl-card__header {
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.82rem;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f9fafb;
  border-radius: 10px 10px 0 0;
}

.tl-card__body {
  padding: 16px;
}

.tl-form-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.tl-form-group {
  flex: 1;
  min-width: 200px;
}

.tl-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.tl-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  font-size: 0.85rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  outline: none;
}

.tl-input:focus {
  border-color: #E2923D;
  box-shadow: 0 0 0 3px rgba(226, 146, 61, .15);
}

.tl-form-actions {
  display: flex;
  gap: 8px;
}

/* ────────────── Table ────────────── */
.tl-table-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
}

.tl-table-scroll {
  overflow-y: auto;
  max-height: 60vh;
}

.tl-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .85rem;
}

.tl-table thead tr {
  background: #E2923D;
  position: sticky;
  top: 0;
  z-index: 2;
}

.tl-th {
  padding: 12px 16px;
  text-align: center;
  color: #fff;
  font-weight: 700;
  font-size: .72rem;
  text-transform: uppercase;
  letter-spacing: .05em;
  border: none;
}

.tl-th--id {
  width: 80px;
}

.text-left {
  text-align: left;
}

.tl-td {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  text-align: center;
  vertical-align: middle;
}

.tl-td--id {
  color: #9ca3af;
  font-size: 0.75rem;
}

.tl-td--name {
  text-align: left;
  font-weight: 600;
  color: #111827;
}

.tl-city-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tl-row:hover .tl-td {
  background: #fff8f1;
}

.tl-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: .72rem;
  font-weight: 700;
}

.tl-pill--green {
  background: #dcfce7;
  color: #166534;
}

.tl-empty {
  text-align: center;
  padding: 48px;
  color: #9ca3af;
  font-size: .88rem;
}
</style>
